import React from "react"
import Animated from "react-native-reanimated"
import { View } from "react-native"
import { abbreviateCurrency } from "@/utils/NumberUtils"
import { type PropertyType } from "@/generated/graphql"
import { type AnalysisType } from "@/queries/analysis"
import useCompsData from "@/hooks/useCompsData"
import useUpdateDimming from "@/hooks/useUpdateDimming"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import BFText from "@/components/standard/BFText"

interface AnalysisHeaderCompactProps {
  analysis: AnalysisType
}

const AnalysisHeaderCompact: React.FC<AnalysisHeaderCompactProps> = ({
  analysis,
}) => {
  const insets = useSafeAreaInsets()
  const property: PropertyType | undefined = analysis?.property
  const { animatedDimStyle } = useUpdateDimming()
  const arv: number = analysis?.afterRepairValue || 0
  const profitVal: number = analysis?.profitPotential || 0

  const { selectedCompCount, totalCompCount, averagePricePerSqft } =
    useCompsData(property)

  const { hasNotch, isWeb } = useDeviceAttributes()

  return (
    <View
      style={{
        paddingTop: hasNotch ? 56 : insets.top,
        minHeight: hasNotch ? 100 : 80,
        alignItems: isWeb ? "flex-end" : "flex-start",
        flex: 1,
      }}
      className="flex-row w-full border-b border-light-grey pb-3 mb-1 px-14"
    >
      <View className="justify-center w-full bg-transparent">
        <View className="flex-row items-start justify-around w-full">
          <View className="flex-1">
            <BFText className="text-[11px] font-bold text-gray-500 font-spacegrotesk text-center">
              EST ARV
            </BFText>
            <Animated.View style={animatedDimStyle}>
              <BFText className="text-[13px] font-figtree-bold text-center">
                {abbreviateCurrency(arv)}
              </BFText>
            </Animated.View>
          </View>

          <CompactVerticalDivider />

          <View className="items-center flex-1">
            <BFText className="text-[11px] text-gray-500 font-spacegrotesk text-center">
              PROFIT
            </BFText>
            <Animated.View style={animatedDimStyle}>
              <BFText className="text-[13px] font-figtree-bold text-center">
                {abbreviateCurrency(profitVal)}
              </BFText>
            </Animated.View>
          </View>

          <CompactVerticalDivider />

          <View className="items-center flex-1">
            <BFText className="text-[11px] text-gray-500 font-spacegrotesk text-center">
              COMPS
            </BFText>
            <Animated.View style={animatedDimStyle}>
              <BFText className="text-[13px] font-figtree-bold text-center">
                {selectedCompCount}/{totalCompCount}
              </BFText>
            </Animated.View>
          </View>

          <CompactVerticalDivider />

          <View className="items-center flex-1">
            <BFText className="text-[11px] text-gray-500 font-spacegrotesk text-center">
              AVG $/SF
            </BFText>
            <Animated.View style={animatedDimStyle}>
              <BFText className="text-[13px] font-figtree-bold text-center">
                ${Math.round(averagePricePerSqft)}
              </BFText>
            </Animated.View>
          </View>
        </View>
      </View>
    </View>
  )
}

export default AnalysisHeaderCompact

const CompactVerticalDivider = () => (
  <View
    style={{
      height: 10,
      width: 1,
      backgroundColor: "#D1D5DB",
      alignSelf: "center",
    }}
  />
)
